<template>
  <div
    class="empty-placeholder"
    :class="{ loading: props.loading, disabled: props.disabled }"
    @click="handleClick"
  >
    <div v-if="hasIcon" class="placeholder-icon">
      <slot name="icon" />
    </div>
    <template v-if="props.loading">
      <inline-loader />
      <span>{{ props.loadingMessage }}</span>
    </template>
    <div v-else class="content">
      <span class="main">
        <slot />
      </span>
      <span v-if="hasSub" class="sub">
        <slot name="sub" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";

// Props
interface Props {
  loading?: boolean;
  loadingMessage?: string;
  disabled?: boolean;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingMessage: "",
  disabled: false,
  dynamicMountChildMixin_parentComponent: () => ({}),
  dynamicMountChildMixin_isDynamicallyMounted: false,
});

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

// Emits
const emit = defineEmits<{
  click: [];
}>();

const slots = useSlots();

// Computed
const hasIcon = computed(() => {
  return !!slots.icon;
});

const hasSub = computed(() => {
  return !!slots.sub;
});

// Methods
const handleClick = () => {
  emit("click");
};
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: $spacing-07 0;
  flex: 1;
  max-width: 100%;

  &.loading {
    position: relative;
    display: flex;
    min-height: 7.5rem;
    flex-flow: column wrap;
    justify-content: center;
    align-items: center;
    text-align: center;
    @include carbon--type-style("helper-text-01");

    span {
      margin-top: $spacing-05;
      margin-left: auto;
      margin-right: auto;
      z-index: z-map("inline-loader");
      color: $text-02;
    }

    .inline-loader {
      border-radius: 10px;
      background-color: $skeleton-01;

      .loader {
        .dots {
          top: 35%;
        }
      }
    }
  }

  &.disabled {
    background-color: $disabled-01;
    border-radius: 10px;
  }

  .placeholder-icon {
    margin-top: $spacing-05;
  }

  .bx--link {
    @include carbon--type-style("body-short-01", true);
  }

  .content {
    margin: $spacing-05 $spacing-07;

    > span {
      display: flex;
      flex-flow: column;
      align-items: center;
      text-align: center;
      color: $text-03;

      &.main {
        @include carbon--type-style("productive-heading-01", true);
      }

      &.sub {
        @include carbon--type-style("body-short-01", true);
        margin-top: $spacing-05;
      }
    }
  }
}
</style>
